/**
 * The shipped control tuning — one place, so "feel" is data rather than numbers scattered
 * through the input layer.
 *
 * These constants are what the milestone-1 report ("hand-flying with the keyboard feels
 * clunky") was about, and `axes.test.ts` asserts the resulting behaviour rather than the
 * numbers themselves: a 60 ms tap must move the control by only a few percent, a held key must
 * still reach the stop in about half a second, and a release must settle on exact neutral
 * inside a third of a second without rebounding. Retune freely — the tests describe the feel
 * the aircraft is expected to have, and will say so if a change breaks it.
 *
 * Why each axis differs:
 *   - **Roll** is the fastest. Ailerons are the control a pilot uses most and most lightly,
 *     and a roll input that lags feels worse than any other.
 *   - **Pitch** is slower and has the widest fine zone: it is the axis where over-control is
 *     punished hardest, because it is the one holding the aeroplane at a speed.
 *   - **Yaw** sits between them, with the widest fine zone of all — rudder is almost always
 *     wanted in small amounts (a boot of rudder is a specific manoeuvre, not a correction).
 *   - **Throttle and trim** are settings, not controls: no fine zone, no self-centring, and
 *     linear, because "near idle" and "near neutral trim" are ordinary places to be rather
 *     than a spring's rest position.
 */

import type { KeyboardAxisOptions } from './axes.js';

/** Elevator. */
export const PITCH_AXIS: KeyboardAxisOptions = {
  expo: 0.35,
  rampRatePerS: 2.2,
  fineZone: 0.28,
  fineRateScale: 0.28,
  reversalBoost: 2.4,
  returnHalfLifeS: 0.085,
  minReturnRatePerS: 1.0,
};

/** Ailerons. */
export const ROLL_AXIS: KeyboardAxisOptions = {
  expo: 0.4,
  rampRatePerS: 3.0,
  fineZone: 0.26,
  fineRateScale: 0.24,
  reversalBoost: 2.4,
  returnHalfLifeS: 0.075,
  minReturnRatePerS: 1.1,
};

/** Rudder. */
export const YAW_AXIS: KeyboardAxisOptions = {
  expo: 0.35,
  rampRatePerS: 2.4,
  fineZone: 0.3,
  fineRateScale: 0.3,
  reversalBoost: 2.2,
  returnHalfLifeS: 0.09,
  minReturnRatePerS: 1.0,
};

/**
 * Throttle lever: 0…1, stays where it is put, linear.
 *
 * Tuned from the milestone-2 report that it was "too slow". Holding the key sweeps idle to full
 * in about a second — roughly how fast a hand moves a real lever, and fast enough that going
 * around does not feel like waiting for permission. A tap steps 5 %, which is the setting a
 * pilot actually wants when they are nudging cruise power, and which an integrated axis cannot
 * give you: with ramping alone the size of a tap is however long your finger was on the key.
 *
 * `tapWindowS` is what keeps the two from fighting. Ramping does not start until the key has
 * been held that long, so 0.05 (the tap) and the ramp never add up, and a hold still reaches the
 * stop in 0.12 + 0.95/1.1 ≈ 0.98 s.
 */
export const THROTTLE_AXIS: KeyboardAxisOptions = {
  selfCentring: false,
  rampRatePerS: 1.1,
  tapStep: 0.05,
  tapWindowS: 0.12,
  min: 0,
  max: 1,
  initialValue: 0,
};

/** Elevator trim wheel: slow, stays where it is put, linear. */
export const TRIM_AXIS: KeyboardAxisOptions = {
  selfCentring: false,
  rampRatePerS: 0.35,
};

/**
 * Rudder trim.
 *
 * Slower than elevator trim and stepped, because rudder trim is a set-and-forget control: it is
 * adjusted once for a power setting and then left alone. On a real trainer it is often a tab you
 * bend on the ground, so anything faster than this would be generous rather than realistic.
 */
export const RUDDER_TRIM_AXIS: KeyboardAxisOptions = {
  selfCentring: false,
  rampRatePerS: 0.22,
  tapStep: 0.02,
  tapWindowS: 0.15,
};

/**
 * Aileron trim.
 *
 * The same shape as rudder trim, and for the same reason: a lateral trim tab cancels a standing
 * wing-heaviness — unequal fuel, one pilot in the left seat, a rigging offset — and is then left
 * alone. Slightly finer steps than the rudder's because the aileron's trim authority is a quarter
 * of its range rather than a third, so an identical step would be a coarser fraction of a smaller
 * control.
 */
export const AILERON_TRIM_AXIS: KeyboardAxisOptions = {
  selfCentring: false,
  rampRatePerS: 0.2,
  tapStep: 0.015,
  tapWindowS: 0.15,
};

/**
 * Mouse yoke.
 *
 * The yoke is *positional*: it does not self-centre, exactly like the real thing. What it is
 * measured against is the pointer position at the moment the yoke was engaged, not the middle
 * of the screen — engaging a screen-centred yoke while the cursor happened to be near an edge
 * used to hand the aircraft full deflection instantly.
 */
export const MOUSE_YOKE = {
  /**
   * Pointer travel that produces full deflection, in window-normalised units (the window
   * spans −1…1, so 0.6 means a little under a third of the window from the capture point).
   */
  travel: 0.6,
  /** Expo on the yoke, for the same reason the keyboard has it. */
  expo: 0.3,
} as const;
